import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { parseDollarsToCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormAction, FormGrid, SelectField, TextAreaField } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { Notice, Section, Status, Table, money, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';
import type { CustomersLocationState } from './Customers.tsx';

interface Detail {
  customer: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    date_of_birth: string | null;
    has_account: boolean;
    marketing_opt_in: boolean;
    notes: string | null;
    created_at: string;
    balance_cents: number;
    anonymized_at?: string | null;
    can_delete?: boolean;
    delete_blocked_reason?: string | null;
    can_erase?: boolean;
  };
  orders: {
    id: string;
    order_number: string;
    status: string;
    pickup_at: string;
    amount_due_cents: number;
    discount_cents: number;
    credit_applied_cents: number;
    summary: string | null;
  }[];
  ledger: {
    id: string;
    kind: string;
    amount_cents: number;
    reason: string | null;
    order_number: string | null;
    expires_at: string | null;
    created_at: string;
    actor: string | null;
  }[];
  birthdays: {
    id: string;
    reward_year: number;
    status: string;
    eligible_until: string;
    order_number: string | null;
  }[];
  feedback: {
    id: string;
    overall: number | null;
    would_order: string | null;
    comment: string | null;
    order_number: string | null;
    created_at: string;
  }[];
}

interface EraseResult {
  ok: boolean;
  erased: {
    orders_kept: number;
    saved_cards: number;
    birthday_rewards: number;
    login_unlinked: boolean;
    credit_balance_cents: number;
    guest_records: number;
    stripe_customers_deleted: number;
    stripe_customers_not_deleted: number;
  };
}

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const againLabel = (v: string | null) =>
  v === 'YES' ? 'Yes' : v === 'MAYBE' ? 'Maybe' : v === 'NO' ? 'No' : '';

/** "SOME_CODE" as "Some code", for values without a plain label below. */
const humanize = (code: string) => {
  const t = code.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const CREDIT_KIND: Record<string, string> = {
  EARN: 'Earned',
  BIRTHDAY: 'Birthday credit',
  REDEEM: 'Used on an order',
  EXPIRE: 'Expired',
  ADJUST: 'Adjusted by staff',
  SERVICE_RECOVERY: 'Service recovery',
  REVERSAL: 'Reversed',
};

const BIRTHDAY_STATUS: Record<string, string> = {
  ISSUED: 'Not used yet',
  REDEEMED: 'Used',
  EXPIRED: 'Expired',
  REVOKED: 'Canceled',
};

/** DELETE sends no body, so the optional reason travels as ?reason= (the API reads both). */
const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;

function eraseMessage(r: EraseResult['erased']): string {
  const parts = [
    r.orders_kept > 0
      ? `Personal details erased. ${plural(r.orders_kept, 'order stays', 'orders stay')} for accounting.`
      : 'Personal details erased.',
  ];
  if (r.saved_cards > 0)
    parts.push(`${plural(r.saved_cards, 'saved card', 'saved cards')} removed.`);
  if (r.guest_records > 0)
    parts.push(
      `${plural(r.guest_records, 'guest checkout', 'guest checkouts')} with the same email erased too.`,
    );
  if (r.stripe_customers_not_deleted > 0)
    parts.push(
      'Stripe has not confirmed removing their saved cards yet. Crave keeps trying automatically, so there is nothing you need to do.',
    );
  return parts.join(' ');
}

export function CustomerDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useStaff();
  const canRemove = can('records.delete') && can('customers.manage');
  const q = useQuery({
    queryKey: ['customer', id],
    queryFn: async () => (await api.get<Detail>(`/admin/customers/${id}`)).data,
  });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const fail = (e: unknown) =>
    setMsg({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [reason, setReason] = useState('');
  const [recovery, setRecovery] = useState(true);
  const [confirming, setConfirming] = useState<'erase' | 'delete' | null>(null);
  const adjust = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ balance_cents: number }>(`/admin/customers/${id}/credit`, body),
    onSuccess: (r) => {
      setMsg({ kind: 'ok', text: `Balance is now ${money(r.data.balance_cents)}.` });
      setAmount('');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: fail,
  });
  const [notes, setNotes] = useState<string | null>(null);
  const saveNotes = useMutation({
    mutationFn: () => api.patch(`/admin/customers/${id}`, { notes: notes ?? '' }),
    onSuccess: () => setMsg({ kind: 'ok', text: 'Notes saved.' }),
    onError: fail,
  });

  if (q.isError) return <Notice kind="error">Could not load this customer.</Notice>;
  if (!q.data) return <p>Loading…</p>;
  const { customer: c, orders, ledger, birthdays, feedback } = q.data;
  const erased = Boolean(c.anonymized_at);
  const realName = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;
  const name = erased ? 'Erased customer' : realName;
  const canErase = c.can_erase ?? !erased;
  const canDeleteRecord = c.can_delete === true;
  const unusedBirthdays = birthdays.filter((b) => b.status !== 'REDEEMED' && !b.order_number);

  const erase = async (why: string | undefined) => {
    const r = await api.post<EraseResult>(
      `/admin/customers/${id}/erase`,
      why ? { reason: why } : {},
    );
    setNotes(null);
    setMsg({ kind: 'ok', text: eraseMessage(r.data.erased) });
    void qc.invalidateQueries({ queryKey: ['customer', id] });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };

  return (
    <Section
      title={name}
      right={
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <HistoryLink entityType="customers" entityId={c.id} name={name} />
          <Link to="/customers" className="crave-back-link">
            All customers
          </Link>
        </div>
      }
    >
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {erased && !msg && (
        <Notice>
          This customer’s personal details were erased {when(c.anonymized_at)}. Their orders stay
          for accounting without a name.
        </Notice>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          gap: 12,
        }}
      >
        <Card>
          {erased ? (
            <>
              <div>Name, email, phone and birthday erased</div>
              <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
                Customer since {when(c.created_at)}
              </div>
            </>
          ) : (
            <>
              <div style={{ overflowWrap: 'anywhere' }}>{c.email}</div>
              <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
                {c.phone ?? 'No phone'} · {c.has_account ? 'Has an account' : 'Guest checkout only'}
              </div>
              <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
                Birthday {c.date_of_birth ?? 'not set'} · Customer since {when(c.created_at)}
              </div>
            </>
          )}
        </Card>
        <Card>
          <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>Crave Credit</div>
          <div style={{ fontSize: 30, fontWeight: 800 }}>{money(c.balance_cents)}</div>
        </Card>
      </div>

      {c.has_account && !erased && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const cents = parseDollarsToCents(amount);
              if (!cents) {
                setMsg({ kind: 'error', text: 'Enter an amount like 5 or 5.50.' });
                return;
              }
              adjust.mutate({
                amount_cents: direction === 'add' ? cents : -cents,
                reason,
                service_recovery: direction === 'add' && recovery,
              });
            }}
            style={{ display: 'grid', gap: 8 }}
          >
            <FormGrid min={160}>
              <SelectField
                label="Adjust credit"
                options={[
                  ['add', 'Add'],
                  ['remove', 'Remove'],
                ]}
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'add' | 'remove')}
              />
              <Field
                label="Amount ($)"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              <Field
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={3}
              />
              {direction === 'add' && (
                <FormAction>
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      minHeight: 'var(--touch)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={recovery}
                      onChange={(e) => setRecovery(e.target.checked)}
                      style={{ width: 20, height: 20, margin: 0, flex: 'none' }}
                    />
                    Service recovery
                  </label>
                </FormAction>
              )}
            </FormGrid>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button type="submit" disabled={adjust.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Card>
      )}

      <h3 style={{ margin: 0 }}>Orders</h3>
      <Table
        head={['Order', 'Pickup', 'Status', 'Items', 'Paid', 'Birthday / credit']}
        rowKeys={orders.map((o) => o.id)}
        rows={orders.map((o) => [
          <Link to={`/orders/${o.id}`}>{o.order_number}</Link>,
          when(o.pickup_at),
          <Status s={o.status} />,
          o.summary ?? '',
          money(o.amount_due_cents),
          [
            o.discount_cents ? `−${money(o.discount_cents)}` : '',
            o.credit_applied_cents ? `credit −${money(o.credit_applied_cents)}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
        ])}
        empty="No orders."
      />

      <h3 style={{ margin: 0 }}>Crave Credit history</h3>
      <Table
        head={['When', 'What', 'Amount', 'Order', 'Reason', 'By']}
        wrapColumns={[1, 4]}
        rows={ledger.map((t) => [
          when(t.created_at),
          CREDIT_KIND[t.kind] ?? humanize(t.kind),
          money(t.amount_cents),
          t.order_number ?? '',
          t.reason ?? '',
          t.actor ?? '',
        ])}
        empty="No credit activity."
      />

      {birthdays.length > 0 && (
        <>
          <h3 style={{ margin: 0 }}>Birthday pizzas</h3>
          <Table
            head={['Year', 'Status', 'Good until', 'Order']}
            rows={birthdays.map((b) => [
              String(b.reward_year),
              BIRTHDAY_STATUS[b.status] ?? humanize(b.status),
              b.eligible_until,
              b.order_number ?? '',
            ])}
          />
        </>
      )}

      {feedback.length > 0 && (
        <>
          <h3 style={{ margin: 0 }}>Feedback</h3>
          <Table
            head={['When', 'Order', 'Overall', 'Again?', 'Comment']}
            wrapColumns={[4]}
            rows={feedback.map((f) => [
              when(f.created_at),
              f.order_number ?? '',
              f.overall ? `${f.overall}/5` : '',
              againLabel(f.would_order),
              f.comment ?? '',
            ])}
          />
        </>
      )}

      {!erased && (
        <Card>
          <TextAreaField
            label="Private notes"
            rows={3}
            value={notes ?? c.notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div style={{ marginTop: 8 }}>
            <Button
              variant="secondary"
              onClick={() => saveNotes.mutate()}
              disabled={notes === null || saveNotes.isPending}
            >
              Save notes
            </Button>
          </div>
        </Card>
      )}

      {canRemove && (canErase || canDeleteRecord || c.delete_blocked_reason) && (
        <Card>
          <div style={{ display: 'grid', gap: 10 }}>
            <h3 style={{ margin: 0 }}>Personal details</h3>
            {canErase && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
                Erasing removes their name, email, phone, birthday, notes and saved cards, here and
                at Stripe. Their orders stay, with totals, for accounting.
              </p>
            )}
            {canDeleteRecord ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
                They have no orders, comps, feedback or Crave Credit history, so the whole record
                can be deleted.
              </p>
            ) : (
              c.delete_blocked_reason && (
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
                  {c.delete_blocked_reason}
                </p>
              )
            )}
            {(canErase || canDeleteRecord) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canErase && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setMsg(null);
                      setConfirming('erase');
                    }}
                  >
                    Erase personal details
                  </Button>
                )}
                {canDeleteRecord && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setMsg(null);
                      setConfirming('delete');
                    }}
                  >
                    Delete customer
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      <ConfirmDelete
        open={confirming === 'erase'}
        onClose={() => setConfirming(null)}
        title={`Erase personal details for ${realName}?`}
        description="Their orders stay, with totals, for accounting, but no longer show who placed them. Guest checkouts with the same email are erased too."
        consequencesTitle="This removes:"
        consequences={[
          'Name, email, phone and birthday',
          ...(c.notes ? ['Private notes'] : []),
          'Saved cards, here and at Stripe',
          ...(unusedBirthdays.length > 0
            ? [plural(unusedBirthdays.length, 'unused birthday pizza', 'unused birthday pizzas')]
            : []),
          ...(c.balance_cents > 0 ? [`Use of their ${money(c.balance_cents)} Crave Credit`] : []),
          ...(c.has_account
            ? ['The link to their Crave login (the login itself is not deleted)']
            : []),
        ]}
        confirmText={realName}
        askReason
        actionLabel="Erase details"
        pendingLabel="Erasing…"
        onConfirm={({ reason: why }) => erase(why)}
      />

      <ConfirmDelete
        open={confirming === 'delete'}
        onClose={() => setConfirming(null)}
        title={erased ? 'Delete this erased customer record?' : `Delete ${realName}?`}
        description={
          erased
            ? 'Their personal details are already erased, and they have no orders, comps or feedback, so the empty record can go.'
            : 'They have no orders, comps, feedback or Crave Credit history, so the whole customer record goes.'
        }
        consequences={
          erased
            ? undefined
            : ['Name, email, phone, birthday and notes', 'Saved cards, here and at Stripe']
        }
        // An erased record has no name left to type (its email is a placeholder address).
        confirmText={erased ? undefined : realName}
        askReason
        onConfirm={async ({ reason: why }) => {
          await api.delete(withReason(`/admin/customers/${id}`, why));
        }}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['customers'] });
          const state: CustomersLocationState = {
            deleted: erased ? 'the erased customer record' : realName,
          };
          void navigate('/customers', { replace: true, state });
          setTimeout(() => qc.removeQueries({ queryKey: ['customer', id] }), 0);
        }}
        alternative={
          canErase
            ? {
                label: 'Erase personal details',
                description:
                  'You can erase their personal details instead. Their orders stay for accounting.',
                pendingLabel: 'Erasing…',
                onClick: ({ reason: why }) => erase(why),
              }
            : undefined
        }
      />
    </Section>
  );
}
